import { AudienceFilterInputType, AudienceFilterOperator } from './components/audience.model';
import { WidgetConditionType, WidgetSimpleInfo } from '../campaign/widgets.model';
import {
  SessionCondition,
  SessionConditionCampaignEventConfig,
  SessionConditionConfig,
  SessionConditionConfigGroup,
  SessionConditionLanguages,
  SessionConditionOperatingSystems,
  SessionConditionTranslations,
  SessionConditionType,
} from './components/session-condition-group.model';
import { TranslateService } from '@ngx-translate/core';
import { Tier } from 'app/shared/account/tier.model';
import {
  comparisonLabelKey,
  comparisonOperator,
  isValuelessType,
  SessionConditionComparison,
  toComparison,
} from './session-condition-comparison';

export default class SessionConditionHelpers {
  public static CampaignSessionConditionTypes = [
    SessionConditionType.CampaignOpened,
    SessionConditionType.CampaignClickthrough,
    SessionConditionType.CampaignConversion,
    SessionConditionType.CampaignDismissal,
    SessionConditionType.CampaignSurveyResponse,
  ];

  public static DeviceSessionConditionTypes = [
    SessionConditionType.OnlyDesktop,
    SessionConditionType.OnlyMobile,
    SessionConditionType.Tablet,
    SessionConditionType.MobileOrTablet,
  ];

  public static getTranslationForSessionConditionType(
    sessionCondition: SessionCondition,
    translateService: TranslateService,
  ): string {
    const currentCondition = SessionConditionHelpers.getSessionConditionConfig(sessionCondition.type);
    if (!currentCondition) {
      return undefined;
    }
    if (toComparison(sessionCondition) === SessionConditionComparison.IsNot) {
      return translateService.instant(
        `audience.session-condition-groups.conditionTypeNegated.${sessionCondition.type}`,
      );
    }
    const visitCount = SessionConditionHelpers.returningVisitorText(sessionCondition);
    if (visitCount) {
      return translateService.instant(visitCount.key, { count: visitCount.count });
    }
    return translateService.instant(currentCondition.titleKey);
  }

  public static describeTranslatedCondition(
    translations: SessionConditionTranslations,
    translateService: TranslateService,
  ): string {
    const parts = [translations.type, translations.operator, translations.value];
    if (translations.campaignEventOperator) {
      parts.push(
        translations.campaignEventOperator,
        translations.campaignEventValue,
        translateService.instant('generic.times').toLowerCase(),
      );
    }
    return parts.filter(Boolean).join(' ');
  }

  /** Returning visitor's comparison and count are part of its type text. */
  private static returningVisitorText(sessionCondition: SessionCondition): { key: string; count: number } | undefined {
    if (sessionCondition.type !== SessionConditionType.ReturningVisitor) {
      return undefined;
    }
    const count = Number(sessionCondition.value);
    if (!sessionCondition.value || !Number.isFinite(count)) {
      return undefined;
    }
    const prefix = 'audience.session-condition-groups.returningVisitorVisits.';
    if (toComparison(sessionCondition) === SessionConditionComparison.LessThan) {
      return { key: prefix + 'LessThan', count };
    }
    return { key: prefix + (count === 1 ? 'MoreThanOne' : 'MoreThan'), count };
  }

  public static getSessionConditionConfig(sessionConditionType: SessionConditionType): SessionConditionConfig {
    const allConfigGroups = SessionConditionHelpers.getAllSessionConditionConfigGroups();
    for (let i = 0; i < allConfigGroups.length; i++) {
      for (let y = 0; y < allConfigGroups[i].conditions.length; y++) {
        const currentGroup = allConfigGroups[i];
        const currentConfig = currentGroup.conditions[y];
        if (currentConfig.type === sessionConditionType) {
          return currentConfig;
        }
      }
    }
    return undefined;
  }

  public static translateSessionCondition(
    condition: SessionCondition,
    skipTranslatingValue = false,
    translateService: TranslateService,
    allWidgets: Array<WidgetSimpleInfo>,
  ): SessionCondition {
    condition.conditionTranslations = condition.conditionTranslations ? condition.conditionTranslations : {};
    condition.conditionTranslations.type = SessionConditionHelpers.getTranslationForSessionConditionType(
      condition,
      translateService,
    );
    condition.conditionTranslations.operator = SessionConditionHelpers.getShortTranslationForSessionConditionOperator(
      condition,
      translateService,
    );
    if (skipTranslatingValue !== true) {
      condition.conditionTranslations.value = SessionConditionHelpers.getTranslationForSessionConditionValue(
        condition,
        translateService,
        allWidgets,
      );
    }
    if (this.CampaignSessionConditionTypes.indexOf(condition.type) !== -1) {
      condition.conditionTranslations.campaignEventOperator = translateService.instant(
        'audience.session-condition-groups.campaignEventCountOperators.' + condition.operator,
      );
      const conditionCampaignEventConfig = this.parseCampaignEventConditionValue(condition);
      condition.conditionTranslations.campaignEventValue = '' + conditionCampaignEventConfig.times;
    }
    return condition;
  }

  public static getShortTranslationForSessionConditionOperator(
    sessionCondition: SessionCondition,
    translateService: TranslateService,
  ): string {
    if (!sessionCondition.value) {
      return undefined;
    }
    const comparison = toComparison(sessionCondition);
    if (comparison) {
      return SessionConditionHelpers.getShortTranslationForComparison(comparison, translateService);
    }
    if (!sessionCondition.operator) {
      return undefined;
    }
    if (this.CampaignSessionConditionTypes.indexOf(sessionCondition.type) !== -1) {
      return '=';
    }
    if (sessionCondition.type === SessionConditionType.JsExpression) {
      return undefined;
    }
    if (sessionCondition.operator === AudienceFilterOperator.Equals) {
      return '=';
    }
    if (sessionCondition.operator === AudienceFilterOperator.NotEquals) {
      return '!=';
    }
    if (
      sessionCondition.operator === AudienceFilterOperator.GreaterThan ||
      sessionCondition.operator === AudienceFilterOperator.MoreThan
    ) {
      return '>';
    }
    if (sessionCondition.operator === AudienceFilterOperator.LessThan) {
      return '<';
    }
    if (sessionCondition.operator === AudienceFilterOperator.Exists) {
      if ('true' === sessionCondition.value) {
        return translateService.instant('audience.operators.Exists');
      } else if ('false' === sessionCondition.value) {
        return translateService.instant('audience.operators.NotExists');
      } else {
        return translateService.instant('generic.error');
      }
    }
    return translateService.instant('audience.operators.' + sessionCondition.operator);
  }

  /** Undefined where the type text already says it. */
  private static getShortTranslationForComparison(
    comparison: SessionConditionComparison,
    translateService: TranslateService,
  ): string | undefined {
    switch (comparison) {
      case SessionConditionComparison.Is:
      case SessionConditionComparison.IsNot:
      case SessionConditionComparison.MoreThan:
      case SessionConditionComparison.LessThan:
        return undefined;
      case SessionConditionComparison.Equals:
        return '=';
      case SessionConditionComparison.DoesNotEqual:
        return '!=';
      default:
        return translateService.instant(comparisonLabelKey(comparison));
    }
  }

  public static getTranslationForSessionConditionValue(
    sessionCondition: SessionCondition,
    translateService: TranslateService,
    allWidgets: Array<WidgetSimpleInfo>,
  ): string {
    if (sessionCondition.operator === AudienceFilterOperator.Exists) {
      return undefined;
    }
    if (SessionConditionHelpers.returningVisitorText(sessionCondition) || isValuelessType(sessionCondition.type)) {
      return undefined;
    }
    if (sessionCondition.type === SessionConditionType.JsExpression) {
      return undefined;
    }
    if (this.CampaignSessionConditionTypes.indexOf(sessionCondition.type) !== -1) {
      const conditionValue = this.parseCampaignEventConditionValue(sessionCondition);
      return this.translateCampaignEventConditionValue(conditionValue.campaignId, translateService, allWidgets);
    }
    if (sessionCondition.type === SessionConditionType.LoggedIn) {
      return translateService.instant(sessionCondition.value === 'true' ? 'generic.yes' : 'generic.no');
    }
    if (sessionCondition.value) {
      if (sessionCondition.value.indexOf(',') !== -1) {
        sessionCondition.value = sessionCondition.value.split(',').join(', ');
      }
    }
    return sessionCondition.value;
  }

  public static translateCampaignEventConditionValue(
    campaignId: number,
    translateService: TranslateService,
    allWidgets: Array<WidgetSimpleInfo>,
  ): string {
    const foundWidget = allWidgets.find((w) => w.campaignId === campaignId);
    if (foundWidget) {
      return foundWidget.campaignName ? foundWidget.campaignName : translateService.instant('campaign.unnamed');
    }
    return campaignId + ' (' + translateService.instant('generic.deleted') + ')';
  }

  public static getPlaceholderKeyForConditionAndOperator(
    conditionConfig: SessionConditionConfig,
    conditionType: SessionConditionType,
    filterOperator: AudienceFilterOperator,
    accountDomain: string,
    translateService: TranslateService,
  ): string {
    if (conditionConfig && conditionConfig.placeholderKey) {
      return conditionConfig.placeholderKey;
    }
    if (!conditionType || !filterOperator) {
      return undefined;
    }
    if (conditionType === SessionConditionType.Domain && !!accountDomain) {
      return translateService.instant('generic.example') + ': ' + accountDomain;
    }
    const inputType = SessionConditionHelpers.getInputTypeForConditionAndOperator(conditionType, filterOperator);
    if (inputType === AudienceFilterInputType.TextField) {
      return 'audience.session-condition-groups.placeholders.textField';
    }
  }

  /** None for types without supportedOperators, whatever operator is stored. */
  public static getInputTypeForCondition(condition: SessionCondition): AudienceFilterInputType {
    if (!SessionConditionHelpers.getSessionConditionConfig(condition.type)?.supportedOperators?.length) {
      return undefined;
    }
    return SessionConditionHelpers.getInputTypeForConditionAndOperator(
      condition.type,
      SessionConditionHelpers.getValueInputOperator(condition),
    );
  }

  /** The comparison's operator when it has one, so the input follows the comparison shown. */
  public static getValueInputOperator(condition: SessionCondition): AudienceFilterOperator {
    const comparison = toComparison(condition);
    return (comparison && comparisonOperator(comparison)) ?? condition.operator;
  }

  public static getInputTypeForConditionAndOperator(
    conditionType: SessionConditionType,
    filterOperator: AudienceFilterOperator,
  ): AudienceFilterInputType {
    if (!conditionType || !filterOperator) {
      return undefined;
    }

    if (filterOperator === AudienceFilterOperator.Exists) {
      return AudienceFilterInputType.BooleanDropdown;
    }

    if (
      (filterOperator === AudienceFilterOperator.MoreThan ||
        filterOperator === AudienceFilterOperator.GreaterThan ||
        filterOperator === AudienceFilterOperator.LessThan) &&
      !this.isSingleChoiceDropdownConditionType(conditionType)
    ) {
      return AudienceFilterInputType.Number;
    }

    if (filterOperator === AudienceFilterOperator.AnyOf || filterOperator === AudienceFilterOperator.NotAnyOf) {
      return AudienceFilterInputType.MultiChoiceDropdown;
    }

    if (
      conditionType === SessionConditionType.OperatingSystem &&
      (filterOperator === AudienceFilterOperator.Equals || filterOperator === AudienceFilterOperator.NotEquals)
    ) {
      return AudienceFilterInputType.MultiChoiceDropdown;
    }

    if (this.isSingleChoiceDropdownConditionType(conditionType)) {
      return AudienceFilterInputType.SingleChoiceDropdown;
    }
    if (filterOperator === AudienceFilterOperator.Equals || filterOperator === AudienceFilterOperator.NotEquals) {
      if (conditionType === SessionConditionType.LoggedIn) {
        return AudienceFilterInputType.BooleanDropdown;
      }
      if (
        conditionType === SessionConditionType.UTMCampaign ||
        conditionType === SessionConditionType.BrowserLanguage
      ) {
        return AudienceFilterInputType.SingleChoiceDropdown;
      } else if (conditionType === SessionConditionType.JsExpression) {
        return AudienceFilterInputType.JavascriptTextArea;
      }
      return AudienceFilterInputType.TextField;
    }

    if (
      filterOperator === AudienceFilterOperator.StartsWith ||
      filterOperator === AudienceFilterOperator.EndsWith ||
      filterOperator === AudienceFilterOperator.Contains ||
      filterOperator === AudienceFilterOperator.NotContains
    ) {
      return AudienceFilterInputType.TextField;
    }

    throw new Error('Unmapped pair of conditionType: ' + conditionType + ' and operator: ' + filterOperator);
  }

  public static conditionConfigToCondition(conditionConfig: SessionConditionConfig): SessionCondition {
    if (!conditionConfig) {
      return undefined;
    }
    const sessionCondition = {
      type: conditionConfig.type,
      operator: conditionConfig.defaultOperator,
      value: conditionConfig.defaultValue,
      show: true,
      conditionConfig: conditionConfig,
    } as SessionCondition;
    return sessionCondition;
  }

  public static getAudienceSessionConditionConfigGroups(): Array<SessionConditionConfigGroup> {
    return SessionConditionHelpers.getAllSessionConditionConfigGroups().map((group) => ({
      ...group,
      conditions: group.conditions.filter((condition) => !condition.widgetOnly),
    }));
  }

  /*
   * This is the old code that we will need to refactor
   */
  public static getAllSessionConditionConfigGroups(): Array<SessionConditionConfigGroup> {
    return [
      {
        type: 'group-source',
        conditions: [
          {
            type: SessionConditionType.OtherSocialMediaVisit,
            icon: 'group',
            titleKey: `audience.session-condition-groups.conditionType.${WidgetConditionType.OtherSocialMediaVisit}`,
            descriptionKey: 'campaign.settings.conditions.social-media-descr',
            defaultShow: true,
            defaultValue: null,
            requiresPopup: false,
            defaultOperator: undefined,
          } as SessionConditionConfig,
          {
            type: SessionConditionType.FacebookVisit,
            svgIcon: 'tb:facebook',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.FacebookVisit}`,
            descriptionKey: 'campaign.settings.conditions.facebook-descr',
            defaultShow: true,
            defaultValue: null,
            requiresPopup: false,
            defaultOperator: undefined,
          } as SessionConditionConfig,
          {
            type: SessionConditionType.InstagramVisit,
            svgIcon: 'tb:instagram',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.InstagramVisit}`,
            descriptionKey: 'campaign.settings.conditions.instagram-descr',
            defaultShow: true,
            defaultValue: null,
            requiresPopup: false,
            defaultOperator: undefined,
          } as SessionConditionConfig,
          {
            type: SessionConditionType.UTMCampaign,
            icon: 'format_underlined',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.UTMCampaign}`,
            descriptionKey: 'campaign.settings.conditions.utm-campaign-descr',
            defaultShow: true,
            defaultValue: null,
            requiresPopup: true,
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [
              AudienceFilterOperator.Equals,
              AudienceFilterOperator.StartsWith,
              AudienceFilterOperator.EndsWith,
              AudienceFilterOperator.Contains,
            ],
          } as SessionConditionConfig,
          {
            type: SessionConditionType.UTMSource,
            icon: 'format_underlined',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.UTMSource}`,
            descriptionKey: 'campaign.settings.conditions.utm-source-descr',
            defaultShow: true,
            defaultValue: null,
            requiresPopup: true,
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [
              AudienceFilterOperator.Equals,
              AudienceFilterOperator.StartsWith,
              AudienceFilterOperator.EndsWith,
              AudienceFilterOperator.Contains,
            ],
          } as SessionConditionConfig,
          {
            type: SessionConditionType.UTMMedium,
            icon: 'format_underlined',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.UTMMedium}`,
            descriptionKey: 'campaign.settings.conditions.utm-medium-descr',
            defaultShow: true,
            defaultValue: null,
            requiresPopup: true,
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [
              AudienceFilterOperator.Equals,
              AudienceFilterOperator.StartsWith,
              AudienceFilterOperator.EndsWith,
              AudienceFilterOperator.Contains,
            ],
          } as SessionConditionConfig,
          {
            type: SessionConditionType.MarketingEmailVisit,
            icon: 'email',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.MarketingEmailVisit}`,
            descriptionKey: 'campaign.settings.conditions.email-marketing-descr',
            defaultShow: true,
            defaultValue: null,
            requiresPopup: false,
            defaultOperator: undefined,
          } as SessionConditionConfig,
        ],
      },
      {
        type: 'group-browser',
        conditions: [
          {
            type: SessionConditionType.BrowserLanguage,
            icon: 'translate',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.BrowserLanguage}`,
            descriptionKey: 'campaign.settings.conditions.browser-lang-descr',
            defaultShow: true,
            defaultValue: 'English',
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
          } as SessionConditionConfig,
          {
            type: SessionConditionType.QueryParam,
            icon: 'http',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.QueryParam}`,
            descriptionKey: 'campaign.settings.conditions.query-param-descr',
            leadInKey: 'campaign.settings.conditions.query-param-lead-in',
            defaultShow: true,
            defaultValue: undefined,
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [
              AudienceFilterOperator.Equals,
              AudienceFilterOperator.StartsWith,
              AudienceFilterOperator.EndsWith,
              AudienceFilterOperator.Contains,
            ],
            placeholderKey: `audience.session-condition-groups.placeholders.${SessionConditionType.QueryParam}`,
          } as SessionConditionConfig,
          {
            type: SessionConditionType.CookieExists,
            icon: 'code',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.CookieExists}`,
            descriptionKey: 'campaign.settings.conditions.cookie-exist-descr',
            defaultShow: true,
            defaultValue: undefined,
            defaultOperator: AudienceFilterOperator.Equals,
            disabledOnTiers: [Tier.Essential],
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.Contains],
          } as SessionConditionConfig,
          {
            type: SessionConditionType.CookieEquals,
            icon: 'code',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.CookieEquals}`,
            descriptionKey: 'campaign.settings.conditions.cookie-value-descr',
            defaultShow: true,
            defaultValue: undefined,
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [AudienceFilterOperator.Equals],
            disabledOnTiers: [Tier.Essential],
            placeholderKey: `audience.session-condition-groups.placeholders.${SessionConditionType.CookieEquals}`,
          } as SessionConditionConfig,
          {
            type: SessionConditionType.OnlyDesktop,
            icon: 'desktop_windows',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.OnlyDesktop}`,
            descriptionKey: 'campaign.settings.conditions.desktop-only-descr',
            defaultShow: true,
            defaultValue: undefined,
            defaultOperator: undefined,
          } as SessionConditionConfig,
          {
            type: SessionConditionType.OnlyMobile,
            icon: 'phone_iphone',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.OnlyMobile}`,
            descriptionKey: 'campaign.settings.conditions.mobile-only-descr',
            defaultShow: true,
            defaultValue: undefined,
            defaultOperator: undefined,
          } as SessionConditionConfig,
          {
            type: SessionConditionType.Tablet,
            icon: 'tablet_mac',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.Tablet}`,
            descriptionKey: 'campaign.settings.conditions.tablet-only-descr',
            defaultShow: true,
            defaultValue: undefined,
            defaultOperator: undefined,
            isNew: true,
          } as SessionConditionConfig,
          {
            type: SessionConditionType.MobileOrTablet,
            icon: 'devices_other',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.MobileOrTablet}`,
            descriptionKey: 'campaign.settings.conditions.mobile-or-tablet-descr',
            defaultShow: true,
            defaultValue: undefined,
            defaultOperator: undefined,
            isNew: true,
          } as SessionConditionConfig,
          {
            type: SessionConditionType.OperatingSystem,
            icon: 'devices',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.OperatingSystem}`,
            descriptionKey: 'campaign.settings.conditions.operating-system-descr',
            defaultShow: true,
            defaultValue: SessionConditionOperatingSystems.Windows,
            requiresPopup: true,
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            isNew: true,
          } as SessionConditionConfig,
          {
            type: SessionConditionType.Domain,
            icon: 'web',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.Domain}`,
            descriptionKey: 'campaign.settings.conditions.domain-descr',
            defaultShow: true,
            defaultValue: undefined,
            defaultOperator: AudienceFilterOperator.Equals,
            disabledOnTiers: [Tier.Essential],
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
          } as SessionConditionConfig,
          {
            type: SessionConditionType.JsExpression,
            icon: 'code',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.JsExpression}`,
            descriptionKey: 'campaign.settings.conditions.js-expr-descr',
            defaultShow: true,
            defaultValue: undefined,
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [AudienceFilterOperator.Equals],
            disabledOnTiers: [Tier.Essential],
          } as SessionConditionConfig,
          {
            type: SessionConditionType.NewVisitor,
            icon: 'person',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.NewVisitor}`,
            descriptionKey: 'campaign.settings.conditions.new-visitor-descr',
            defaultShow: true,
            defaultValue: null,
            requiresPopup: false,
            defaultOperator: undefined,
          } as SessionConditionConfig,
          {
            type: SessionConditionType.ReturningVisitor,
            icon: 'person',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.ReturningVisitor}`,
            descriptionKey: 'campaign.settings.conditions.returning-visitor-descr',
            leadInKey: 'campaign.settings.conditions.returning-visitor-lead-in',
            defaultShow: true,
            defaultValue: '1',
            requiresPopup: false,
            defaultOperator: AudienceFilterOperator.MoreThan,
            supportedOperators: [AudienceFilterOperator.MoreThan, AudienceFilterOperator.LessThan],
          } as SessionConditionConfig,
          {
            type: SessionConditionType.IdentifiedEmailMarketing,
            icon: 'person',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.IdentifiedEmailMarketing}`,
            descriptionKey: 'campaign.settings.conditions.identified-email-marketing-descr',
            descriptionKey2: 'campaign.settings.conditions.identified-email-marketing-descr2',
            defaultShow: true,
            defaultValue: undefined,
            requiresPopup: false,
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [AudienceFilterOperator.Equals, AudienceFilterOperator.NotEquals],
            disabledOnTiers: [Tier.Essential],
            formFieldType: AudienceFilterInputType.SingleChoiceDropdown,
          } as SessionConditionConfig,
          {
            type: SessionConditionType.LoggedIn,
            icon: 'login',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.LoggedIn}`,
            descriptionKey: 'campaign.settings.conditions.logged-in-descr',
            defaultShow: true,
            defaultValue: 'true',
            requiresPopup: false,
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [AudienceFilterOperator.Equals],
            disabledOnTiers: [Tier.Essential],
            isNew: true,
            widgetOnly: true,
          } as SessionConditionConfig,
        ],
      },
      {
        type: 'group-campaign',
        conditions: [
          {
            type: SessionConditionType.CampaignOpened,
            icon: 'visibility',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.CampaignOpened}`,
            descriptionKey: 'campaign.settings.conditions.campaign-opened-descr',
            defaultShow: true,
            defaultValue: JSON.stringify({
              campaignId: undefined,
              times: 1,
            } as SessionConditionCampaignEventConfig),
            requiresPopup: false,
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [AudienceFilterOperator.Equals],
            disabledOnTiers: [Tier.Essential],
          } as SessionConditionConfig,
          {
            type: SessionConditionType.CampaignConversion,
            icon: 'stars',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.CampaignConversion}`,
            descriptionKey: 'campaign.settings.conditions.campaign-conversion-descr',
            defaultShow: true,
            defaultValue: JSON.stringify({
              campaignId: undefined,
              times: 1,
            } as SessionConditionCampaignEventConfig),
            requiresPopup: false,
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [AudienceFilterOperator.Equals],
            disabledOnTiers: [Tier.Essential],
          } as SessionConditionConfig,
          {
            type: SessionConditionType.CampaignClickthrough,
            icon: 'mouse',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.CampaignClickthrough}`,
            descriptionKey: 'campaign.settings.conditions.campaign-clickthrough-descr',
            defaultShow: true,
            defaultValue: JSON.stringify({
              campaignId: undefined,
              times: 1,
            } as SessionConditionCampaignEventConfig),
            requiresPopup: false,
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [AudienceFilterOperator.Equals],
            disabledOnTiers: [Tier.Essential],
          } as SessionConditionConfig,
          {
            type: SessionConditionType.CampaignDismissal,
            icon: 'cancel',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.CampaignDismissal}`,
            descriptionKey: 'campaign.settings.conditions.campaign-dismissal-descr',
            defaultShow: true,
            defaultValue: JSON.stringify({
              campaignId: undefined,
              times: 1,
            } as SessionConditionCampaignEventConfig),
            requiresPopup: false,
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [AudienceFilterOperator.Equals],
            disabledOnTiers: [Tier.Essential],
          } as SessionConditionConfig,
          /*{
            type: SessionConditionType.CampaignSurveyResponse,
            icon: 'stars',
            titleKey: `audience.session-condition-groups.conditionType.${SessionConditionType.CampaignSurveyResponse}`,
            descriptionKey:
              'campaign.settings.conditions.campaign-survey-response-descr',
            defaultShow: true,
            defaultValue: JSON.stringify({
              campaignId: undefined,
              times: 1,
            } as SessionConditionCampaignEventConfig),
            requiresPopup: false,
            defaultOperator: AudienceFilterOperator.Equals,
            supportedOperators: [AudienceFilterOperator.Equals],
          } as SessionConditionConfig,*/
        ],
      },
    ] as Array<SessionConditionConfigGroup>;
  }

  public static parseCampaignEventConditionValue(
    sessionCondition: SessionCondition,
  ): SessionConditionCampaignEventConfig {
    if (isNaN(+sessionCondition.value)) {
      return JSON.parse(sessionCondition.value) as SessionConditionCampaignEventConfig;
    } else {
      return {
        campaignId: +sessionCondition.value,
        times: 1,
      } as SessionConditionCampaignEventConfig;
    }
  }

  public static getBrowserLanguages(): Array<{ key: string; value: string }> {
    const map: Array<{ key: string; value: string }> = [];

    for (const n in SessionConditionLanguages) {
      if (typeof SessionConditionLanguages[n] === 'string') {
        map.push({
          key: SessionConditionLanguages[n],
          value: SessionConditionLanguages[n],
        });
      }
    }
    return map;
  }

  public static getOperatingSystems(): Array<{ key: string; value: string }> {
    return Object.values(SessionConditionOperatingSystems).map((name) => ({ key: name, value: name }));
  }

  public static widgetsToChoosableOptions(widgets: Array<WidgetSimpleInfo>): Array<{ key: string; value: string }> {
    const map: Array<{ key: string; value: string }> = [];

    if (widgets) {
      for (const widget of widgets) {
        map.push({ key: '' + widget.id, value: widget.dynamicName });
      }
    }
    return map;
  }

  public static widgetsToCampaignsAsChoosableOptions(
    widgets: Array<WidgetSimpleInfo>,
    translateService: TranslateService,
  ): Array<{ key: string; value: string }> {
    if (widgets) {
      widgets.forEach((w) => {
        if (!w.campaignName || w.campaignName === '') {
          w.campaignName = translateService.instant('campaign.unnamed');
        }
      });
      widgets.sort((a, b) => (a.campaignName.toLowerCase() > b.campaignName.toLowerCase() ? 1 : -1));
      const map: Array<{ key: string; value: string }> = [];

      if (widgets) {
        for (const widget of widgets) {
          if (map.findIndex((item) => item.key === '' + widget.campaignId) === -1) {
            map.push({
              key: '' + widget.campaignId,
              value: widget.campaignName,
            });
          }
        }
      }
      return map;
    }
  }

  public static isSingleChoiceDropdownConditionType(conditionType) {
    return (
      conditionType === SessionConditionType.CampaignOpened ||
      conditionType === SessionConditionType.CampaignClickthrough ||
      conditionType === SessionConditionType.CampaignConversion ||
      conditionType === SessionConditionType.CampaignDismissal ||
      conditionType === SessionConditionType.CampaignSurveyResponse ||
      conditionType === SessionConditionType.IdentifiedEmailMarketing
    );
  }
}
